/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { EnvironmentService } from '../../core/services/environment.service';
import { HealthService } from '../../core/services/health.service';
import { ServiceConfiguration, ServiceName } from '../../../assets/config/environment';
import { ApiErrorResponse } from '../model/api-error.model';
import { DialogMessageRequest, DialogMessageResponse, DialogState } from '../model/dialog.model';
import { TranslationLanguage, TranslationParticipant } from '../model/translation.model';
import { selectTranslationEnabled } from '../../configuration/+state/configuration.selectors';

export { ApiErrorResponse } from '../model/api-error.model';
export { DialogCapabilities, DialogState } from '../model/dialog.model';
export { TranslationCapabilityState, TranslationLanguage, TranslationMessage, TranslationParticipant, TranslationResult } from '../model/translation.model';

@Injectable({ providedIn: 'root' })
export class TextTranslationService extends HealthService {

    constructor(protected http: HttpClient, protected env: EnvironmentService, private store: Store) {
        super(
            http,
            ServiceName.TEXT_TRANSLATION,
            env.environment?.SERVICES?.[ServiceName.TEXT_TRANSLATION]?.length
                ? env.environment.SERVICES[ServiceName.TEXT_TRANSLATION]
                : [{ url: '', rackId: '' } as ServiceConfiguration],
            '/actuator/health/readiness',
            false
        );

        if (TextTranslationService.isMasterFlagEnabled(env)) {
            this.store.select(selectTranslationEnabled).subscribe((agencyEnabled) => this.syncHealthChecks(agencyEnabled));
        }
    }

    private static isMasterFlagEnabled(env: EnvironmentService): boolean {
        return !!env.environment?.SERVICES?.[ServiceName.TEXT_TRANSLATION]?.length && !!env.environment?.featureFlags?.translationsEnabled;
    }

    private syncHealthChecks(agencyEnabled: boolean): void {
        if (agencyEnabled && !this.active) {
            this.active = true;
            this.initializeHealthChecks();
        } else if (!agencyEnabled && this.active) {
            this.active = false;
            this.cancelScheduledHealthChecks();
        }
    }

    private handleApiError(err: HttpErrorResponse): Observable<never> {
        const apiError = err.error as ApiErrorResponse;
        const errorCode = apiError?.errorCode ?? 'N/A';
        const errorMessage = apiError?.errorMessage ?? err.message;
        return throwError(() => new Error(`[${errorCode}] ${errorMessage}`));
    }

    translateMessage(
        dialogId: string,
        messageId: string,
        text: string,
        participant: TranslationParticipant
    ): Observable<{ originalText: string | null; translatedText: string | null; sourceLanguage: string | null; targetLanguage: string | null }> {
        const url = `${this.activeUrl$.value}/text-translation/dialogs/${dialogId}/messages`;
        const body: DialogMessageRequest = { participant, messages: [{ messageId, text }] };
        return this.http
            .post<DialogMessageResponse[]>(url, body, { observe: 'response' })
            .pipe(
                map((resp: HttpResponse<DialogMessageResponse[]>) => {
                    const responses = resp.body ?? [];
                    const response = responses.find((r) => r.messageId === messageId);
                    const translation = response?.capabilities?.translation;
                    const originalText = response?.text ?? null;
                    const translatedText = translation?.applied && translation.translatedText ? translation.translatedText : null;
                    const sourceLanguage = translation?.sourceLanguage ?? null;
                    const targetLanguage = translation?.targetLanguage ?? null;
                    return { originalText, translatedText, sourceLanguage, targetLanguage };
                }),
                catchError((err: HttpErrorResponse) => {
                    console.error(`TS.translateMessage — ERROR HTTP ${err.status} dialogId=${dialogId} messageId=${messageId} url=${url}`, err.error);
                    return this.handleApiError(err);
                })
            );
    }

    getDialogState(dialogId: string): Observable<DialogState> {
        const url = `${this.activeUrl$.value}/text-translation/dialogs/${dialogId}`;
        return this.http.get<DialogState>(url, { observe: 'response' }).pipe(
            map((resp: HttpResponse<DialogState>) => {
                return resp.body!;
            }),
            catchError((err: HttpErrorResponse) => {
                console.error(`TS.getDialogState — ERROR HTTP ${err.status} url=${url}`, err.error);
                return this.handleApiError(err);
            })
        );
    }

    getDialogLanguages(): Observable<TranslationLanguage[]> {
        const url = `${this.activeUrl$.value}/text-translation/languages`;
        console.info(`TS.getDialogLanguages — GET ${url}`);
        return this.http.get<TranslationLanguage[]>(url, { observe: 'response' }).pipe(
            map((resp: HttpResponse<TranslationLanguage[]>) => {
                return resp.body ?? [];
            }),
            catchError((err: HttpErrorResponse) => {
                console.error(`TS.getDialogLanguages — ERROR HTTP ${err.status} url=${url}`, err.error);
                return this.handleApiError(err);
            })
        );
    }

    patchDialogLanguage(dialogId: string, overrideLanguageCode: string): Observable<void> {
        const url = `${this.activeUrl$.value}/text-translation/dialogs/${dialogId}/translation/settings`;
        return this.http.patch<void>(url, { overrideLanguageCode }, { observe: 'response' }).pipe(
            map(() => void 0),
            catchError((err: HttpErrorResponse) => {
                console.error(`TS.patchDialogLanguage ERROR HTTP ${err.status} dialogId=${dialogId} overrideLanguageCode=${overrideLanguageCode} url=${url}`, err.error);
                return this.handleApiError(err);
            })
        );
    }
}
